import { NotificationArgsProps, notification as antdNotification, theme } from 'antd';
import { FC, ReactNode, createContext, useCallback, useContext } from 'react';
import { CheckCircleFill, ExclamationCircleFill, InfoCircleFill, XCircleFill } from 'react-bootstrap-icons';

import { NotificationInstance } from 'antd/es/notification/interface';
import './AppMessage.scss';

type NotificationType = Exclude<keyof NotificationInstance, 'destroy'>;

export interface AppMessageConfigProps extends Omit<
  NotificationArgsProps,
  'message' | 'description' | 'icon' | 'closeIcon' | 'placement' | 'closable' | 'btn'
> {}

export type AppMessageControlsApi = {
  [x in NotificationType]: (message: ReactNode, configOverride?: AppMessageConfigProps) => void;
};

const AppMessageContext = createContext<AppMessageControlsApi>({
  open: () => {},
  error: () => {},
  info: () => {},
  success: () => {},
  warning: () => {},
});

const AppMessage: FC<{ children: ReactNode }> & { useControls: () => AppMessageControlsApi } = ({ children }) => {
  const { token } = theme.useToken();
  const [notificationApi, notificationContextHolder] = antdNotification.useNotification({
    duration: 3,
    stack: {
      threshold: 3,
    },
  });

  const defineIcon = useCallback((type: NotificationType) => {
    if (type === 'success') {
      return <CheckCircleFill size={20} color={token.colorSuccess} />;
    }

    if (type === 'error') {
      return <XCircleFill size={20} color={token.colorError} />;
    }

    if (type === 'info') {
      return <InfoCircleFill size={20} color={token.colorInfo} />;
    }

    if (type === 'warning') {
      return <ExclamationCircleFill size={20} color={token.colorWarning} />;
    }

    return <></>;
  }, []);

  const handleOpen = useCallback((message: ReactNode, type: NotificationType, configOverride?: AppMessageConfigProps) => {
    const { className, ...otherProps } = configOverride || {};

    notificationApi[type]({
      ...otherProps,
      className: `app-message ${className}`,
      closeIcon: <XCircleFill size={14} />,
      icon: defineIcon(type),
      closable: true,
      message,
      placement: 'bottom',
    });
  }, []);

  return (
    <AppMessageContext.Provider
      value={(['error', 'info', 'success', 'warning'] as NotificationType[]).reduce((acc, type) => {
        acc[type] = (message, configOverride) => handleOpen(message, type, configOverride);
        return acc;
      }, {} as AppMessageControlsApi)}
    >
      {notificationContextHolder}
      {children}
    </AppMessageContext.Provider>
  );
};

AppMessage.useControls = () => {
  return useContext(AppMessageContext);
};

export { AppMessage };
